import get from 'lodash/get';
import chunk from 'lodash/chunk';
import i18n from './../../utils/i18n';
import {
    FOOTBALL,
    RUGBY_LEAGUE,
    RUGBY_UNION,
    GAA_HURLING,
    GAA_FOOTBALL,
    TENNIS,
    BADMINTON,
    BASKETBALL,
    ICE_HOCKEY,
    AMERICAN_FOOTBALL,
    SNOOKER,
    BOXING,
    DARTS,
    CRICKET,
    GOLF,
    BASEBALL,
    HANDBALL,
    VOLLEYBALL,
    ESPORTS,
    TABLE_TENNIS,
    MMA,
    AUSTRALIAN_RULES,
    MOTORSPORT,

    FOOTBALL_NORMAL_TIME_TEXT,
    FOOTBALL_FIRST_HALF_TIME_IN_MINUTES,
    FOOTBALL_SECOND_HALF_TIME_IN_MINUTES,
    FOOTBALL_SECOND_HALF_BREAK_TEXT,
    FOOTBALL_EXTRA_TIME_TEXT,
    FOOTBALL_INJURY_TIME_TEXT,
    FOOTBALL_PENALTIES_TEXT,

    FIRST_HALF_BREAK_TEXT,

    SET_TEXT,
    SET_CURRENT_SERVER_MODIFIER,
    SET_CURRENT_SCORE_MODIFIER,
    PAST_SET_SCORE,
    SET_SERVER_MODIFIER,
    SET_SERVER_ONE,
    SET_SERVER_TWO,
    SCORE_IN_SETS,

    GAA_EXTRA_TIME_TEXT,
    GAA_TIME_TEXT,

    DARTS_LEGS_MODIFIER,
    DARTS_LEG_CODE,
    DARTS_SET_CODE,
    DARTS_ENDED_CODE,
    DARTS_LEG_STATUS_TEXT,
    DARTS_SET_STATUS_TEXT,
    DARTS_GAME_ON_STATUS_TEXT,
    DARTS_ENDED_STATUS_TEXT,

    CRICKET_STATUS_TEXT,
    CRICKET_BATTING_MODIFIER,
    HANDBALL_PENALTIES_I18N_KEY,
    HANDBALL_FT_I18N_KEY,
    HANDBALL_HT_I18N_KEY,
    HANDBALL_NORMAL_TIME_I18N_KEY,

    SNOOKER_STATUS_FRAME_TEXT,

    BOXING_STATUS_ROUND_TEXT,

    VOLLYBALL_STATUS_PAUSED_TEXT,
    VOLLYBALL_STATUS_TEXT,

    SPORT_QUARTER_STATUS_TEXT,
    SPORT_QUARTER_ONLY_TEXT,
    SPORT_MINUTES_TEXT,
    SPORT_PERIOD_ONLY_TEXT,
    SPORT_OVERTIME,
    SPORT_OVERTIME_STATUS_TEXT,

    ICE_HOCKEY_STATUS_TEXT,

    I18N_PREFIX,
    ICE_HOCKEY_MINS_STATUS_TEXT,
    ICE_HOCKEY_PAUSED_STATUS_TEXT,
    ICE_HOCKEY_ENDED_STATUS_TEXT,
    ICE_HOCKEY_OVERTIME_STATUS_TEXT,
    ICE_HOCKEY_PENALTIES_STATUS_TEXT,

    MMA_ROUND_I18N_KEY,

    ESPORTS_DECIDER_STATUS,
    GAME_TEXT,
} from './constants';
import {
    EVENT_NOT_STARTED,
} from '../sport-event/constants';
import {
    makeColumnField,
    extractScoreFromEventData,
    extractTimeFromEventData,
    extractSetFromEventData,
    extractTennisScoreFromEventData,
    extractBadmintonScoreFromEventData,
    extractRugbyStatusFromEventData,
    extractGaaStatusFromEventData,
    extractBoxingRoundNumberFromEventData,
    extractSnookerFrameNumberFromEventData,
    extractDartsStatusDataFromEventData,
    extractCricketDataFromEventData,
    extractbaseballInningDataFromEventData,
    extractHandballPeriodFromEventData,
    extractEsportsFromEventData,
    extractTableTennisScoreFromEventData,
    extractAmericanFootballDataFromEventData,
    extractAustralianRulesScoreFromEventData,
    extractLapsFromEventData,
    isFootballFirstHalfInjuryTime,
    isFootballSecondHalfInjuryTime,
    isFirstHalfBreak,
    isFootballSecondHalfBreak,
    isFootballExtraTime,
    isFootballPenalties,
    isHandballPenalties,
    isHandballFullTime,
    isHandballHalfTime,
    formatQuarters,
    formatPeriod,
    isIceHockeyInterval,
    isIceHockeyFullTime,
    isIceHockeyOverTime,
    isIceHockeyAfterOverTime,
    isIceHockeyPenalties,
    isEsportsDecider,
    extractMmaDataFromEventData,
    isMatchStatusAboutToStart,
} from './utils';

/**
 * Normalizes and returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function footballNormalizer(state) {
    const { match_status = EVENT_NOT_STARTED } = state;
    const [homeScore, awayScore] = extractScoreFromEventData(state);
    const { minutes, seconds } = extractTimeFromEventData(state);
    let status = i18n.t(FOOTBALL_NORMAL_TIME_TEXT, { minutes });

    if (isFootballFirstHalfInjuryTime(match_status, minutes, seconds)) {
        const extra = minutes - FOOTBALL_FIRST_HALF_TIME_IN_MINUTES;
        status = i18n.t(FOOTBALL_INJURY_TIME_TEXT, { minutes: FOOTBALL_FIRST_HALF_TIME_IN_MINUTES, extra });
    }

    if (isFootballSecondHalfInjuryTime(match_status, minutes, seconds)) {
        const extra = minutes - FOOTBALL_SECOND_HALF_TIME_IN_MINUTES;
        status = i18n.t(FOOTBALL_INJURY_TIME_TEXT, { minutes: FOOTBALL_SECOND_HALF_TIME_IN_MINUTES, extra });
    }

    if (isFirstHalfBreak(match_status)) {
        status = i18n.t(FIRST_HALF_BREAK_TEXT);
    }

    if (isFootballSecondHalfBreak(match_status)) {
        status = i18n.t(FOOTBALL_SECOND_HALF_BREAK_TEXT);
    }

    if (isFootballExtraTime(match_status)) {
        status = i18n.t(FOOTBALL_EXTRA_TIME_TEXT, { minutes });
    }

    if (isFootballPenalties(match_status)) {
        status = i18n.t(FOOTBALL_PENALTIES_TEXT);
    }

    return {
        status,
        columns: [
            [
                makeColumnField(homeScore, [FOOTBALL]),
                makeColumnField(awayScore, [FOOTBALL]),
            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function rugbyLeagueNormalizer(state) {
    const statusValues = extractRugbyStatusFromEventData(state);

    return {
        status: statusValues.status,
        columns: [
            [
                makeColumnField(statusValues.homeScore, [RUGBY_LEAGUE]),
                makeColumnField(statusValues.awayScore, [RUGBY_LEAGUE]),
            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function rugbyUnionNormalizer(state) {
    const statusValues = extractRugbyStatusFromEventData(state);

    return {
        status: statusValues.status,
        columns: [
            [
                makeColumnField(statusValues.homeScore, [RUGBY_UNION]),
                makeColumnField(statusValues.awayScore, [RUGBY_UNION]),
            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function gaaHurlingNormalizer(state) {
    const statusValues = extractGaaStatusFromEventData(state);
    let i18nKey = GAA_TIME_TEXT;
    if (statusValues.extraMinutes) {
        i18nKey = GAA_EXTRA_TIME_TEXT;
    }

    return {
        status: i18n.t(i18nKey, { minutes: statusValues.minutes, extra: statusValues.extraMinutes }),
        columns: [
            [
                makeColumnField(statusValues.homeScore, [GAA_HURLING]),
                makeColumnField(statusValues.awayScore, [GAA_HURLING]),
            ],
        ],

    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function gaaFootballNormalizer(state) {
    const statusValues = extractGaaStatusFromEventData(state);
    let i18nKey = GAA_TIME_TEXT;
    if (statusValues.extraMinutes !== false) {
        i18nKey = GAA_EXTRA_TIME_TEXT;
    }

    return {
        status: i18n.t(i18nKey, { minutes: statusValues.minutes, extra: statusValues.extraMinutes }),
        columns: [
            [
                makeColumnField(statusValues.homeScore, [GAA_FOOTBALL]),
                makeColumnField(statusValues.awayScore, [GAA_FOOTBALL]),
            ],
        ],

    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @param  {Object} currentState The event data
 * @return {Object} The normalized score board data
 */
function tennisNormalizer(state, currentState) {
    const { set } = extractSetFromEventData(state);
    const currentServer = get(currentState, ['data', 'server']);
    const currentSetScores = get(currentState, ['data', 'setScores']);
    const currentGameScore = get(currentState, ['data', 'gameScore']);
    const {setScores = currentSetScores, gameScore = currentGameScore, server = currentServer} = extractTennisScoreFromEventData(state);
    const [playerOneScore, playerTwoScore] = gameScore;

    const data = { setScores, gameScore, server };

    const columns = [];

    columns.unshift([
        makeColumnField('', [TENNIS, SET_SERVER_MODIFIER, `${String(server) === SET_SERVER_ONE ? SET_CURRENT_SERVER_MODIFIER : ''}`]),
        makeColumnField('', [TENNIS, SET_SERVER_MODIFIER, `${String(server) === SET_SERVER_TWO ? SET_CURRENT_SERVER_MODIFIER : ''}`]),
    ]);

    columns.unshift([
        makeColumnField(playerOneScore, [TENNIS, SET_CURRENT_SCORE_MODIFIER]),
        makeColumnField(playerTwoScore, [TENNIS, SET_CURRENT_SCORE_MODIFIER]),
    ]);

    setScores.forEach(set => {
        columns.unshift(set.split(':').map(score => makeColumnField(score, [TENNIS, PAST_SET_SCORE])));
    });

    return {
        data,
        columns,
        status: i18n.t(SET_TEXT, { set }),
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @param  {Object} currentState The event data
 * @return {Object} The normalized score board data
 */
function badmintonNormalizer(state, currentState) {
    let gameArr = ['', ''];
    let game = '';
    if (state.match_status) {
        gameArr = state.match_status.split('_');
    } else if (state.event_status) {
        gameArr = state.event_status.split('_');
    }
    game = gameArr[1] !== '' ? gameArr[1] : '';
    const currentGameScores = get(currentState, ['data', 'gameScores']) || [];
    const { gameScores = currentGameScores, gameScore } = extractBadmintonScoreFromEventData(state);
    const [playerOneScore = '', playerTwoScore = ''] = gameScore;

    const data = { gameScores, gameScore };

    const columns = [];

    columns.unshift([
        makeColumnField(playerOneScore, [BADMINTON, SET_CURRENT_SCORE_MODIFIER]),
        makeColumnField(playerTwoScore, [BADMINTON, SET_CURRENT_SCORE_MODIFIER]),
    ]);

    gameScores.forEach(game => {
        columns.unshift(game.split(':').map(score => makeColumnField(score, [BADMINTON, PAST_SET_SCORE])));
    });

    const status = game !== '' ? i18n.t(GAME_TEXT, { set: game }) : game;

    return {
        data,
        columns,
        status,
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function mmaNormalizer(state) {
    const { roundNo, matchStatus } = extractMmaDataFromEventData(state);
    let status = '';
    if (roundNo) {
        status = i18n.t(MMA_ROUND_I18N_KEY, { round: roundNo });
    } else if (matchStatus) {
        status = i18n.t(`${I18N_PREFIX}.${matchStatus.toLowerCase()}`, '');
    }

    return {
        status,
        // dummy scores - hidden in css as for boxing
        columns: [
            [
                makeColumnField('0', [MMA]),
                makeColumnField('0', [MMA]),
            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @param  {Object} currentState The event data
 * @return {Object} The normalized score board data
 */
function tableTennisNormalizer(state, currentState) {
    /**
     * @todo https://dedsert.atlassian.net/browse/AM-1773 - very quick and dirty fix to
     * prevent the web app from crashing when we get an unexpected state shape. This is a
     * production bug we noticed and fixed just before release.
     */
    let setArr = ['', ''];
    let set = '';
    if (state.match_status) {
        setArr = state.match_status.split('_');
    } else if (state.event_status) {
        setArr = state.event_status.split('_');
    }
    // eslint-disable-next-line no-magic-numbers
    set = setArr[1];
    const currentSetScores = get(currentState, ['data', 'setScores']) || [];
    const currentGameScore = get(currentState, ['data', 'gameScore']) || [];
    const { setScores = currentSetScores, gameScore = currentGameScore } = extractTableTennisScoreFromEventData(state);
    const [playerOneScore, playerTwoScore] = gameScore;

    const data = { setScores, gameScore };

    const columns = [];

    columns.unshift([
        makeColumnField(playerOneScore, [TABLE_TENNIS, SET_CURRENT_SCORE_MODIFIER]),
        makeColumnField(playerTwoScore, [TABLE_TENNIS, SET_CURRENT_SCORE_MODIFIER]),
    ]);

    setScores.forEach(set => {
        columns.unshift(set.split(':').map(score => makeColumnField(score, [TABLE_TENNIS, PAST_SET_SCORE])));
    });

    return {
        data,
        columns,
        status: i18n.t(GAME_TEXT, { set }),
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function basketballNormalizer(state) {
    const { match_status } = state;
    const [homeScore, awayScore] = extractScoreFromEventData(state);
    const { minutes } = extractTimeFromEventData(state);
    const quarter = formatQuarters(match_status);

    let status = '';
    if (match_status === SPORT_OVERTIME) {
        status = i18n.t(SPORT_OVERTIME_STATUS_TEXT, { minutes });
    } else if (typeof match_status !== 'undefined') {
        status = i18n.t(SPORT_QUARTER_STATUS_TEXT, { quarter, minutes });
    } else {
        status = i18n.t(SPORT_MINUTES_TEXT, { minutes });
    }

    return {
        status,
        columns: [
            [
                makeColumnField(homeScore, [BASKETBALL]),
                makeColumnField(awayScore, [BASKETBALL]),
            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function iceHockeyNormalizer(state) {
    const { match_status } = state;
    const [homeScore, awayScore] = extractScoreFromEventData(state);
    const { minutes } = extractTimeFromEventData(state);
    let status = '';

    if (typeof match_status === 'undefined') {
        status = i18n.t(ICE_HOCKEY_MINS_STATUS_TEXT, { minutes });
    } else if (isIceHockeyFullTime(match_status)) {
        status = i18n.t(ICE_HOCKEY_ENDED_STATUS_TEXT);
    } else if (isIceHockeyInterval(match_status)) {
        status = i18n.t(ICE_HOCKEY_PAUSED_STATUS_TEXT);
    } else if (isIceHockeyOverTime(match_status)) {
        status = i18n.t(ICE_HOCKEY_OVERTIME_STATUS_TEXT, { minutes });
    } else if (isIceHockeyAfterOverTime(match_status)) {
        status = i18n.t(ICE_HOCKEY_PAUSED_STATUS_TEXT);
    } else if (isIceHockeyPenalties(match_status)) {
        status = i18n.t(ICE_HOCKEY_PENALTIES_STATUS_TEXT);
    } else if (formatPeriod(match_status)) {
        const period = formatPeriod(match_status);
        status = i18n.t(ICE_HOCKEY_STATUS_TEXT, { period, minutes });
    } else if (isMatchStatusAboutToStart(match_status)) {
        status = i18n.t(SPORT_PERIOD_ONLY_TEXT, { period: 1 });
    } else {
        status = i18n.t(SPORT_MINUTES_TEXT);
    }

    return {
        status,
        columns: [
            [
                makeColumnField(homeScore, [ICE_HOCKEY]),
                makeColumnField(awayScore, [ICE_HOCKEY]),
            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function americanFootballNormalizer(state) {
    const quarter = extractAmericanFootballDataFromEventData(state);
    const [homeScore, awayScore] = extractScoreFromEventData(state);

    const status = i18n.t(SPORT_QUARTER_ONLY_TEXT, { quarter });

    return {
        columns: [
            [
                makeColumnField(homeScore, [AMERICAN_FOOTBALL]),
                makeColumnField(awayScore, [AMERICAN_FOOTBALL]),
            ],
        ],
        status,
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @param  {Object} currentState The event data
 * @return {Object} The normalized score board data
 */
function australianRulesNormalizer(state) {
    const {
        quarter,
        minutes,
        gameScore,
    } = extractAustralianRulesScoreFromEventData(state);
    const [homeScore, awayScore] = gameScore;
    const status = i18n.t(SPORT_QUARTER_STATUS_TEXT, { quarter, minutes });

    return {
        columns: [
            [
                makeColumnField(homeScore, [AUSTRALIAN_RULES]),
                makeColumnField(awayScore, [AUSTRALIAN_RULES]),
            ],
        ],
        status,
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function snookerNormalizer(state) {
    const frame = extractSnookerFrameNumberFromEventData(state);
    const [homeScore, awayScore] = extractScoreFromEventData(state);
    let status = i18n.t(SNOOKER_STATUS_FRAME_TEXT, { frame });

    return {
        status,
        columns: [
            [
                makeColumnField(homeScore, [SNOOKER]),
                makeColumnField(awayScore, [SNOOKER]),
            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * Boxing does not have a score so using placeholder values
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function boxingNormalizer(state) {
    const round = extractBoxingRoundNumberFromEventData(state);

    const { minutes } = extractTimeFromEventData(state);
    const status = i18n.t(BOXING_STATUS_ROUND_TEXT, { round, minutes: minutes });

    return {
        status,
        columns: [
            [
                makeColumnField('0', [BOXING]),
                makeColumnField('0', [BOXING]),
            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function dartsNormalizer(state) {
    const { round, status, scores } = extractDartsStatusDataFromEventData(state);
    const numberOfDartColumns = 2;
    const legsScoreIndex = 1;
    const columns = chunk(scores, numberOfDartColumns).map((column, index) => {
        const bemModifiers = [DARTS];

        if (!status || index === legsScoreIndex || status === DARTS_LEG_CODE) {
            bemModifiers.push(DARTS_LEGS_MODIFIER);
        }

        return column.map(score => makeColumnField(score, bemModifiers));
    });

    if (!status) {
        return {
            columns,
            status,
        };
    }

    let i18nKey;
    switch (status) {
        case DARTS_LEG_CODE:
            i18nKey = DARTS_LEG_STATUS_TEXT;
            break;
        case DARTS_SET_CODE:
            i18nKey = DARTS_SET_STATUS_TEXT;
            break;
        case DARTS_ENDED_CODE:
            i18nKey = DARTS_ENDED_STATUS_TEXT;
            break;
        default:
            i18nKey = DARTS_GAME_ON_STATUS_TEXT; // avoid undefined key
    }

    return {
        columns,
        status: i18n.t(i18nKey, { leg: round, set: round }),
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function cricketNormalizer(state) {
    const { inning, scores, battingTeamIndex } = extractCricketDataFromEventData(state);

    const columns = [scores.map((score, index) => {
        const bemModifiers = [CRICKET];

        if (index === battingTeamIndex) {
            bemModifiers.push(CRICKET_BATTING_MODIFIER);
        }

        return makeColumnField(score, bemModifiers);
    })];

    return {
        columns,
        status: i18n.t(CRICKET_STATUS_TEXT, { inning }),
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function golfNormalizer(state) {
    const { extra_info, match_status } = state;
    const matchStatus = extra_info?.match_status || match_status;
    const status = matchStatus.toLowerCase().replace(/\s/g, '_');

    return {
        status: i18n.t(`${I18N_PREFIX}.${status}`, matchStatus),
        columns: [
            [
                makeColumnField('0', [GOLF]),
                makeColumnField('0', [GOLF]),
            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function baseballNormalizer(state) {
    let { inning, possess } = extractbaseballInningDataFromEventData(state);
    const [homeScore, awayScore] = extractScoreFromEventData(state);
    let possessionParam;

    switch (possess) {
        case 'Top':
            possessionParam = '$t(score_board.bball_top)';
        break;
        case 'Bottom':
            possessionParam = '$t(score_board.bball_bottom)';
        break;
        default:
            possessionParam = '';
        break;
    }

    return {
        status:  i18n.t(`${I18N_PREFIX}.bball_inning`, { inning: inning, possess: possessionParam }),
        columns: [
            [
                makeColumnField(homeScore, [BASEBALL]),
                makeColumnField(awayScore, [BASEBALL]),

            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function handballNormalizer(state) {
    const { match_status } = state;
    const period = extractHandballPeriodFromEventData(state);
    const [homeScore, awayScore] = extractScoreFromEventData(state);
    const { minutes } = extractTimeFromEventData(state);
    let status = '';

    if (isHandballPenalties(match_status)) {
        status = i18n.t(HANDBALL_PENALTIES_I18N_KEY);
    } else if (isHandballFullTime(match_status)) {
        status = i18n.t(HANDBALL_FT_I18N_KEY);
    } else if (isHandballHalfTime(match_status)) {
        status = i18n.t(HANDBALL_HT_I18N_KEY);
    }
    else {
        status = i18n.t(HANDBALL_NORMAL_TIME_I18N_KEY, { period, minutes });
    }

    return {
        status,
        columns: [
            [
                makeColumnField(homeScore, [HANDBALL]),
                makeColumnField(awayScore, [HANDBALL]),

            ],
        ],
    };
}

/**
 * @param  {Object} state The event data
 * @param  {Object} currentState The event data
 * @return {Object} The normalized score board data
 */
function volleyballNormalizer(state, currentState) {
    const { set } = extractSetFromEventData(state);
    const isSetInplay = set !== 0;
    const sets = isSetInplay
        ? i18n.t(VOLLYBALL_STATUS_TEXT, { set })
        : i18n.t(VOLLYBALL_STATUS_PAUSED_TEXT);

    const currentServer = String(
        (state?.extra_info?.server || currentState?.data?.server)
        || '' // can be empty if event has transitioned fromn pp -> ip
    );
    const allSetScores = state?.extra_info?.setscores?.split('-').map(setScore => setScore.split(':')) || currentState?.data?.setScores || [];
    const previousSetScores = [...allSetScores];
    const currentSetScore = previousSetScores.pop() || [];

    const data = {
        setScores: allSetScores,
        server: currentServer,
    };

    const columns = [];

    columns.unshift([
        makeColumnField('', [VOLLEYBALL, SET_SERVER_MODIFIER, currentServer === SET_SERVER_ONE ? SET_CURRENT_SERVER_MODIFIER : '']),
        makeColumnField('', [VOLLEYBALL, SET_SERVER_MODIFIER, currentServer === SET_SERVER_TWO ? SET_CURRENT_SERVER_MODIFIER : '']),
    ]);

    const [
        currentScoreOne = '',
        currentScoreTwo = '',
    ] = currentSetScore;

    columns.unshift([
        makeColumnField(currentScoreOne, [VOLLEYBALL, SET_CURRENT_SCORE_MODIFIER]),
        makeColumnField(currentScoreTwo, [VOLLEYBALL, SET_CURRENT_SCORE_MODIFIER]),
    ]);

    previousSetScores.forEach(previousSetScore => {
        const [
            playerOneScore,
            playerTwoScore,
        ] = previousSetScore;
        columns.unshift([
            makeColumnField(playerOneScore, [VOLLEYBALL, SCORE_IN_SETS]),
            makeColumnField(playerTwoScore, [VOLLEYBALL, SCORE_IN_SETS]),
        ]);
    });

    return {
        data,
        columns,
        status: sets,
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function eSportsNormaliser(state) {
    const {sb_map_number, sb_best_of_number, sb_period} = extractEsportsFromEventData(state);
    const [homeScore, awayScore] = extractScoreFromEventData(state);
    let status = '';

    if(isEsportsDecider(sb_period)) {
        status = i18n.t(ESPORTS_DECIDER_STATUS);
    } else if (sb_map_number && sb_best_of_number) {
        status = i18n.t(`${I18N_PREFIX}.esports_status`, { sb_map_number, sb_best_of_number });
    }

    return {
        status,
        columns: [
            [
                makeColumnField(homeScore, [ESPORTS]),
                makeColumnField(awayScore, [ESPORTS]),
            ],
        ],
    };
}

/**
 * Returns score board data from event data
 * @param  {Object} state The event data
 * @return {Object} The normalized score board data
 */
function motorSportsNormaliser(state) {
    const laps = extractLapsFromEventData(state);
    const scoreString = i18n.t(`${I18N_PREFIX}.laps_[n]`, {laps});

    return {
        status: scoreString,
        columns: [],
    };
}

const NormalizeStrategies = {
    [FOOTBALL]: footballNormalizer,
    [RUGBY_LEAGUE]: rugbyLeagueNormalizer,
    [RUGBY_UNION]: rugbyUnionNormalizer,
    [GAA_HURLING]: gaaHurlingNormalizer,
    [GAA_FOOTBALL]: gaaFootballNormalizer,
    [MMA]: mmaNormalizer,
    [TENNIS]: tennisNormalizer,
    [BADMINTON]: badmintonNormalizer,
    [BASKETBALL]: basketballNormalizer,
    [ICE_HOCKEY]: iceHockeyNormalizer,
    [AMERICAN_FOOTBALL]: americanFootballNormalizer,
    [SNOOKER]: snookerNormalizer,
    [BOXING]: boxingNormalizer,
    [DARTS]: dartsNormalizer,
    [CRICKET]: cricketNormalizer,
    [GOLF]: golfNormalizer,
    [BASEBALL]: baseballNormalizer,
    [HANDBALL]: handballNormalizer,
    [VOLLEYBALL]: volleyballNormalizer,
    [ESPORTS]: eSportsNormaliser,
    [TABLE_TENNIS]: tableTennisNormalizer,
    [AUSTRALIAN_RULES]: australianRulesNormalizer,
    [MOTORSPORT]: motorSportsNormaliser,
};

export default NormalizeStrategies;
